/**
 * @file Shared three-way payment-route control: one radio group offering
 * AGENCY / DIRECT_UNTRACKED / DIRECT_TRACKED, each option labelled with the
 * party that checks the payment (agency or owner name), used by every host that
 * lets the user set a charge template's route.
 *
 * Flow: host state (`paymentRouteId`) + host-supplied `agencyName` / `ownerName`
 * → `PAYMENT_ROUTE_OPTIONS` (option key + which party it names) → MUI RadioGroup
 * → change event narrowed to a `PaymentRouteId` → host's `onChange` handler,
 * which writes it into its own state / dispatches its own save.
 * Exceptions: none — a change event whose value is not a valid route id is
 * ignored; a missing party name renders the option label with an empty name.
 */
import * as React from "react";
import Component from "../../AbstractComponent";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";
import {
  PAYMENT_ROUTE_OPTIONS,
  PaymentRouteId,
  isPaymentRouteId,
} from "../../../Entity/Entities/Charge/PaymentRoute";

interface PaymentRouteRadioProps {
  value: PaymentRouteId;
  onChange: (paymentRouteId: PaymentRouteId) => void;
  agencyName?: string;
  ownerName?: string;
}

let instanceCount = 0;

export default class PaymentRouteRadio extends Component {
  // One native radio group per mounted instance (two hosts can be on screen at once).
  private groupName: string;

  constructor(public props: PaymentRouteRadioProps) {
    super(props);
    instanceCount += 1;
    this.groupName = `paymentRouteId-${instanceCount}`;
  }

  public handleChange = (event) => {
    const value = Number(event?.target?.value);
    if (isPaymentRouteId(value)) {
      this.props.onChange(value);
    }
  }

  public render() {
    return (
      <FormControl component="fieldset" className="form-element full-size">
        <FormLabel component="legend">
          {this.trans("label-payment-route")}
        </FormLabel>
        <RadioGroup
          name={this.groupName}
          value={String(this.props.value)}
          onChange={this.handleChange}
          style={{ marginTop: "8px" }}
        >
          {PAYMENT_ROUTE_OPTIONS.map(option => (
            <FormControlLabel
              key={option.id}
              value={String(option.id)}
              control={<Radio color="primary" style={{ paddingTop: "2px", paddingBottom: "2px" }} />}
              label={this.trans(option.optionKey, {
                partyName: (option.checkingParty === "owner" ? this.props.ownerName : this.props.agencyName) || "",
              })}
            />
          ))}
        </RadioGroup>
      </FormControl>
    );
  }
}
